import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Box,
  Typography
} from '@mui/material';
import { Grade as GradeIcon } from '@mui/icons-material';
import { assetsApi, ratingApi } from '../../services/api';

const EditAssetRatingDialog = ({ open, onClose, asset, onUpdate }) => {
  const [selectedRatingLevelId, setSelectedRatingLevelId] = useState('');
  const [ratingScheme, setRatingScheme] = useState(null);
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSchemeAndLevels = async () => {
      if (open && asset) {
        setLoading(true);
        setError(null);
        try {
          const response = await ratingApi.getSchemes({
            organization_id: asset.organization_id,
            asset_type_id: asset.asset_type_id,
          });
          const responsePayload = response.data;
          const schemes = Array.isArray(responsePayload)
            ? responsePayload
            : (Array.isArray(responsePayload?.data) ? responsePayload.data : []);

          if (schemes.length > 0) {
            const scheme = schemes[0];
            setRatingScheme(scheme);
            const levelsResponse = await ratingApi.getLevels({ scheme_id: scheme.id });
            const levelsPayload = levelsResponse.data;
            const levels = Array.isArray(levelsPayload) ? levelsPayload : (Array.isArray(levelsPayload?.data) ? levelsPayload.data : []);
            setLevels(levels);
          } else {
            setLevels([]);
            setRatingScheme(null);
          }
          setSelectedRatingLevelId(asset.rating_level_id || '');
        } catch (err) {
          setError('Failed to load rating levels for this asset type.');
          console.error('Error fetching rating scheme and levels:', err);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchSchemeAndLevels();
  }, [open, asset]);

  const handleRatingChange = (event) => {
    setSelectedRatingLevelId(event.target.value);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      await assetsApi.updateAssetRating(asset.id, {
        rating_level_id: selectedRatingLevelId || null,
      });
      onUpdate(); // Callback to refresh asset data in the parent component
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update asset rating.');
      console.error('Error updating asset rating:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <GradeIcon />
        Edit Asset Rating
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        
        <Typography variant="body2" sx={{ mb: 2 }}>
          Manually set the rating for <strong>{asset?.name}</strong>.
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <FormControl fullWidth>
            <InputLabel id="rating-level-select-label">Rating Level</InputLabel>
            <Select
              labelId="rating-level-select-label"
              value={selectedRatingLevelId}
              onChange={handleRatingChange}
              label="Rating Level"
            >
              <MenuItem value="">
                <em>Not Rated</em>
              </MenuItem>
              {levels.map((level) => (
                <MenuItem key={level.id} value={level.id}>
                  {level.name.replace('Cx', 'Rx')} ({level.value})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Save Rating'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditAssetRatingDialog;